import {useLocation, useNavigate, useParams} from 'react-router'
import {useTranslation} from 'react-i18next'
import {PageLayout} from '@/components/layout/PageLayout'
import {PageHeader} from '@/components/layout/PageHeader'
import {useActionModule} from './hooks/useActionModule'
import ActionModuleEditor from './editor/ActionModuleEditor'
import type {ActionModuleKind, ActionModuleResponse} from "@/api/model";

// Full action-module wizard. Two scopes (see `useActionModuleMutations`):
// - `isAdmin`: URL carries `:companyId` → administration endpoints (create/edit/delete).
// - CMU (`!isAdmin`): no `:companyId` → `companies/current` endpoints (edit-only).
export default function ActionModuleEditorPage({ isAdmin = false }: { isAdmin?: boolean }) {
  const { t } = useTranslation('action-modules-wizard')
  const { companyId, actionModuleId } = useParams<{ companyId?: string; actionModuleId?: string}>()
  const navigate = useNavigate()
  const location = useLocation()
  const kind = location.state?.kind as ActionModuleKind | undefined
  const cid = isAdmin && companyId ? Number(companyId) : undefined
  const amId = actionModuleId && actionModuleId !== 'new' ? Number(actionModuleId) : undefined
  const { isLoading, actionModule } = useActionModule({ isAdmin, companyId: cid, actionModuleId: amId, kind: kind })

  const back = () => navigate('/action-modules')

  const openCopy = (copy: ActionModuleResponse) =>
    navigate(`/action-modules/edit/${copy.id}`, { state: { kind: copy.kind } })

  return (
    <PageLayout>
      <PageHeader title={amId ? (actionModule?.name ?? t('page.moduleFallback')) : t('page.newTitle')} />
      <ActionModuleEditor
        key={amId ?? 'new'}
        isAdmin={isAdmin}
        companyId={cid}
        actionModuleId={amId}
        actionModule={actionModule}
        isLoading={isLoading}
        onSaved={back}
        onDeleted={back}
        onCopied={openCopy}
      />
    </PageLayout>
  )
}
